import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTriageBatches } from '../hooks/useTriage';
import { StatusBadge } from '../components/StatusBadge';
import type { TriageBatchSummary } from '../types';

const QUEUE = 'missing-ship-memo';

function ShipMemoBatchCard({ batch }: { batch: TriageBatchSummary }) {
  const progressPercent =
    batch.totalItems > 0 ? Math.round((batch.resolvedItems / batch.totalItems) * 100) : 0;

  const formattedDate = new Date(batch.date + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  if (batch.totalItems === 0) {
    return (
      <div className="flex items-center justify-between bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">{formattedDate}</h3>
          <p className="text-sm text-gray-500">No missing ship memos</p>
        </div>
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
              clipRule="evenodd"
            />
          </svg>
          All clear
        </span>
      </div>
    );
  }

  return (
    <Link
      to={`/ship-memos/${batch.date}`}
      className="block bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md hover:border-gray-300 transition-all"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-gray-900">{formattedDate}</h3>
        <StatusBadge status={batch.status} />
      </div>

      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Progress</span>
          <span className="font-medium text-gray-900">
            {batch.resolvedItems} / {batch.totalItems} memos
          </span>
        </div>

        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all ${
              batch.status === 'complete'
                ? 'bg-green-500'
                : batch.status === 'partial'
                  ? 'bg-blue-500'
                  : 'bg-yellow-500'
            }`}
            style={{ width: `${progressPercent}%` }}
          />
        </div>

        <div className="flex justify-between text-sm pt-1">
          <span className={batch.pendingItems > 0 ? 'text-red-600 font-medium' : 'text-gray-500'}>
            {batch.pendingItems} pending
          </span>
          <span className="text-gray-500">{progressPercent}% complete</span>
        </div>
      </div>
    </Link>
  );
}

export function ShipMemos() {
  const { data: batches, isLoading } = useTriageBatches(QUEUE);

  const stats = useMemo(() => {
    if (!batches) return null;
    return {
      resolvedItems: batches.reduce((sum, b) => sum + b.resolvedItems, 0),
      pendingItems: batches.reduce((sum, b) => sum + b.pendingItems, 0),
    };
  }, [batches]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Missing Ship Memos</h1>
        <p className="mt-1 text-sm text-gray-500">
          Ship memos created in CHQ that never arrived in AirBoss, by day.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-sm font-medium text-gray-500">Resolved</p>
          <p className="mt-1 text-3xl font-semibold text-green-600">
            {isLoading ? (
              <span className="animate-pulse bg-gray-200 h-9 w-20 rounded inline-block" />
            ) : (
              stats?.resolvedItems || 0
            )}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-red-200 p-4">
          <p className="text-sm font-medium text-red-600">Pending</p>
          <p className="mt-1 text-3xl font-semibold text-red-600">
            {isLoading ? (
              <span className="animate-pulse bg-gray-200 h-9 w-20 rounded inline-block" />
            ) : (
              stats?.pendingItems || 0
            )}
          </p>
        </div>
      </div>

      {/* Batch List */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Batches</h2>
        {isLoading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 animate-pulse"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="h-6 bg-gray-200 rounded w-32" />
                  <div className="h-5 bg-gray-200 rounded w-16" />
                </div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-full" />
                  <div className="h-2 bg-gray-200 rounded w-full" />
                  <div className="h-4 bg-gray-200 rounded w-24" />
                </div>
              </div>
            ))}
          </div>
        ) : !batches || batches.length === 0 ? (
          <div className="text-center py-12">
            <h3 className="text-sm font-medium text-gray-900">No batches</h3>
            <p className="mt-1 text-sm text-gray-500">
              No missing ship memo batches found in the last 90 days.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {batches.map((batch) => (
              <ShipMemoBatchCard key={batch.date} batch={batch} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
